import {useState, useCallback, useRef} from 'react';

import {CoreEntityConfirmationDialog} from 'shells/core-entity-confirmation-dialog/core-entity-confirmation-dialog';
import {ArchiveRegularIcon} from 'shells/icon/svg/archive-regular-icon';
import {CalendarSquareRegularActivityIcon} from 'shells/icon/svg/calendar-square-regular-activity-icon';
import {PersonWarningIcon} from 'shells/icon/svg/person-warning-icon';
import {TrashRegularIcon} from 'shells/icon/svg/trash-regular-icon';
import {ContactCard} from 'shells/peep-cards/contact-card/contact-card';
import type {BaseRowStatusBadge} from 'shells/row/base-row/base-row';
import type {ToolbarOption} from 'shells/toolbar/toolbar';

import type {GetContactsForLead_lead_contacts_primaryEdge_node_phones_primary_value as PhoneValue} from 'nutshell-graphql-types';

import {appHistory} from '../../../../history';

import {ConnectButtonsRow} from './connect-buttons-row';
import {getUserEntityFromApiType} from './helpers';
import {HoverableEntityAvatar} from './hoverable-entity-avatar';
import type {ApiEntity} from './types';

interface Props {
    contactId: string;
    name: string | null | undefined;
    contactUrl: string | null | undefined;
    description: string | null | undefined;
    role: string | null | undefined;
    phone: PhoneValue | null | undefined;
    phoneCallerType: string | null | undefined;
    userCanUseSms: boolean | null | undefined;
    sendFromNutshellEnabled: boolean | null | undefined;
    email: string | null | undefined;
    lastContactedTime: string | null | undefined;
    isDuplicate: boolean | null | undefined;
    isFormer?: boolean | null | undefined;
    isPrimary?: boolean | null | undefined;
    showIsPrimaryIcon?: boolean | null | undefined;
    avatarUrl: string | null | undefined;
    parentEntityName: ApiEntity;
    onOpenEmailModal: (emailAddress: string) => void;
    removeContact: (contactId: string, contactParentAccountId?: string) => void;
    contactParentAccount?: string;
    setError?: (arg1: string) => void;
    hasConnectedPhoneIntegration: boolean;
    prependToolbarOptions?: ToolbarOption[];
    successFlashKey?: string;
}

export function ContactRow(props: Props) {
    const {removeContact, showIsPrimaryIcon = true} = props;

    const [isHovered, setIsHovered] = useState<boolean>(false);
    const [forceToolbarOpen, setForceToolbarOpen] = useState<boolean>(false);
    const [isRemoveConfirmationOpen, setIsRemoveConfirmationOpen] = useState<boolean>(false);

    const duplicateRef = useRef<HTMLElement | null | undefined>(null);
    const removeButtonRef = useRef<HTMLElement | null | undefined>(null);

    const handleDuplicateClick = () => {
        if (duplicateRef.current) {
            const $element = $(duplicateRef.current);
            setForceToolbarOpen(true);

            const handlePopoverClose = () => {
                setForceToolbarOpen(false);
                $element.off('popoverWasClosed', handlePopoverClose);
            };

            $element.on('popoverWasClosed', handlePopoverClose);

            Popover.load({
                url: '/de-duplication/popover.json',
                data: {
                    entityName: 'Contacts',
                    entityId: props.contactId,
                    parentEntityName: props.parentEntityName,
                },

                appendTo: $element,
                noLoadingIndicator: true,
                error: function () {
                    props.setError?.('Failed to load duplicate popover');
                    setForceToolbarOpen(false);
                    $element.off('popoverWasClosed', handlePopoverClose);
                },
            });
        }
    };

    const handleMouseEnter = useCallback(() => {
        if (!isHovered) {
            setIsHovered(true);
        }
    }, [isHovered]);

    const handleMouseOut = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        if (e.relatedTarget && e.currentTarget.contains(e.relatedTarget as any)) {
            return;
        }

        setIsHovered(false);
    }, []);

    const handleRemoveContact = useCallback(() => {
        setIsRemoveConfirmationOpen(false);
        setForceToolbarOpen(false);
        removeContact(props.contactId, props.contactParentAccount);
    }, [props.contactId, props.contactParentAccount, removeContact]);

    const handleCancelRemoveContact = useCallback(() => {
        setIsRemoveConfirmationOpen(false);
        setForceToolbarOpen(false);
    }, []);

    const parentEntity = getUserEntityFromApiType(props.parentEntityName);

    const toolbarOptions: ToolbarOption[] = [
        ...(props.prependToolbarOptions || []),
        {
            id: 'contact-schedule-activity',
            tooltipText: 'Schedule activity',
            icon: CalendarSquareRegularActivityIcon,
            onClick: () => {
                appHistory.replace({
                    ...appHistory.location,
                    hash: `/create/activities/${props.contactId}`,
                    state: {modal: true},
                });
            },
        },
        {
            id: 'contact-remove-from-lead',
            tooltipText: `Remove from ${parentEntity}`,
            icon: TrashRegularIcon,
            onClick: () => {
                setForceToolbarOpen(true);
                setIsRemoveConfirmationOpen(true);
            },
            getRef: (ref) => {
                removeButtonRef.current = ref;
            },
        },
    ];

    const statusBadges: BaseRowStatusBadge[] = [];
    if (props.isDuplicate) {
        statusBadges.push({
            text: 'Duplicate?',
            level: 'alert',
            icon: PersonWarningIcon,
            onClick: handleDuplicateClick,
            getRef: (ref) => {
                duplicateRef.current = ref;
            },
        });
    }

    if (props.isFormer) {
        statusBadges.push({
            text: 'Former contact',
            level: 'info--grey',
            icon: ArchiveRegularIcon,
        });
    }

    return (
        <>
            <div onMouseEnter={handleMouseEnter} onMouseOut={handleMouseOut}>
                <ContactCard
                    name={props.name}
                    description={props.description}
                    role={props.role}
                    phoneNumber={props.phone && props.phone.numberFormattedWithCountryCode}
                    phoneExtension={props.phone && props.phone.extension}
                    email={props.email}
                    lastContactedTime={props.lastContactedTime}
                    icon={
                        <HoverableEntityAvatar
                            avatarUrl={props.avatarUrl}
                            entityId={props.contactId}
                            entityName='Contacts'
                            parentEntityName={props.parentEntityName}
                            isHovered={isHovered}
                            isPrimary={showIsPrimaryIcon ? props.isPrimary : undefined}
                        />
                    }
                    toolbarProps={{options: toolbarOptions, forceShowToolbar: forceToolbarOpen}}
                    statusBadges={statusBadges}
                    contactUrl={props.contactUrl}
                    connectButtonsRow={
                        <ConnectButtonsRow
                            id={props.contactId}
                            name={props.name}
                            phone={props.phone}
                            email={props.email}
                            phoneCallerType={props.phoneCallerType}
                            userCanUseSms={props.userCanUseSms}
                            sendFromNutshellEnabled={props.sendFromNutshellEnabled}
                            onOpenEmailModal={props.onOpenEmailModal}
                            hasConnectedPhoneIntegration={props.hasConnectedPhoneIntegration}
                        />
                    }
                    successFlashKey={props.successFlashKey}
                />
            </div>
            {isRemoveConfirmationOpen && (
                <CoreEntityConfirmationDialog
                    anchor={removeButtonRef.current}
                    type='person'
                    parentEntity={parentEntity}
                    onConfirm={handleRemoveContact}
                    onCancel={handleCancelRemoveContact}
                />
            )}
        </>
    );
}
